import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Award, Menu, X, User, LogOut, LayoutDashboard, Star, PlusCircle, Building2, Handshake } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

export default function Navbar() {
  const { user, isAuthenticated, isAdmin, isPartner, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/awards', label: 'Travel Awards' },
    { path: '/twblawards', label: 'TWBL Awards' },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-nav border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group"
            data-testid="navbar-logo"
          >
            <div className="leading-none">
              <span className="font-['Playfair_Display'] text-xl font-semibold text-white group-hover:text-[#D4AF37] transition-colors block">
                Travel-Awards
              </span>
              <span className="text-[#D4AF37] text-xs block mt-0.5 tracking-widest uppercase">
                .org
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#D4AF37]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {isAuthenticated && (
              <Link
                to="/nominate"
                data-testid="nav-link-nominate"
                className={`text-sm font-medium transition-colors ${
                  isActive('/nominate')
                    ? 'text-[#D4AF37]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Nominate
              </Link>
            )}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
                      <span className="text-sm font-semibold text-[#0A0A0A]">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="max-w-[120px] truncate">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-white/10">
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')}
                    className="cursor-pointer hover:bg-white/5"
                    data-testid="user-menu-dashboard"
                  >
                    <Star className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    <span>My Dashboard</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/nominate')}
                    className="cursor-pointer hover:bg-white/5"
                    data-testid="user-menu-nominate"
                  >
                    <PlusCircle className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    <span>Submit Nomination</span>
                  </DropdownMenuItem>
                  {isPartner && (
                    <DropdownMenuItem 
                      onClick={() => navigate('/partner/profile')}
                      className="cursor-pointer hover:bg-white/5"
                      data-testid="user-menu-partner-profile"
                    >
                      <Handshake className="mr-2 h-4 w-4 text-green-400" />
                      <span>My Partner Profile</span>
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        className="cursor-pointer hover:bg-white/5"
                        data-testid="user-menu-admin"
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4 text-[#D4AF37]" />
                        <span>Admin Panel</span>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="cursor-pointer hover:bg-white/5 text-red-400"
                    data-testid="user-menu-logout"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="nav-login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] font-semibold"
                    data-testid="nav-register-btn"
                  >
                    Join Now
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-white/70 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                      : 'text-white/70 hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {isAuthenticated && (
                <>
                  <Link
                    to="/nominate"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-nominate"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/nominate')
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'text-white/70 hover:bg-white/5'
                    }`}
                  >
                    Nominate
                  </Link>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-dashboard"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive('/dashboard')
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'text-white/70 hover:bg-white/5'
                    }`}
                  >
                    Dashboard
                  </Link>
                  {isPartner && (
                    <Link
                      to="/partner/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-partner-profile"
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive('/partner/profile')
                          ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                          : 'text-white/70 hover:bg-white/5'
                      }`}
                    >
                      My Partner Profile
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-admin"
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive('/admin')
                          ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                          : 'text-white/70 hover:bg-white/5'
                      }`}
                    >
                      Admin Panel
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    data-testid="mobile-logout-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium text-red-400 hover:bg-white/5 text-left"
                  >
                    Logout
                  </button>
                </>
              )}
              
              {!isAuthenticated && (
                <div className="flex flex-col gap-2 pt-2 border-t border-white/10 mt-2">
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-login-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/5"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-register-btn"
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-[#D4AF37] text-[#0A0A0A] text-center"
                  >
                    Join Now
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
